<template>
  <Multiselect
    :model-value="modelValue"
    @update:model-value="emit('update:modelValue', $event)"
    :options="roles"
    :searchable="false"
    :required="required"
    :can-clear="false"
    :min="1"
    track-by="value"
    label="label"
    value-prop="value"
    mode="tags"
    :close-on-select="false"
    :error="error"
    :disabled="isLoading"
    :loading="isLoading"
  />
</template>

<script setup lang="ts">
import Multiselect from '@vueform/multiselect';
import { useRoleSelectOptions } from '@infuse/iam/role/queries';
import { computed, watch } from 'vue';
import { useOrganisationRoleSelectOptions } from '@infuse/iam/organisation/queries';
import { useTenantRoleSelectOptions } from '@infuse/iam/tenant/queries';
import { UserLevel } from '@infuse/iam/user/type';
import { useCurrentUserStore } from '@infuse/iam/user/store';

const props = withDefaults(
  defineProps<{
    modelValue: string[];
    required?: boolean;
    error?: boolean;
    level?: UserLevel;
    parentId?: string;
  }>(),
  {
    level: UserLevel.DOMAIN,
    parentId: undefined
  }
);

const currentUserStore = useCurrentUserStore();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
}>();

const { roles: domainRoles, isLoading: isDomainRolesLoading, enabled: enabledDomainRoles } = useRoleSelectOptions();
const {
  roles: organisationRoles,
  fetchOrganisationId,
  isLoading: isOrganisationRolesLoading
} = useOrganisationRoleSelectOptions();
const { roles: tenantRoles, fetchTenantId, isLoading: isTenantRolesLoading } = useTenantRoleSelectOptions();

const roles = computed(() => {
  switch (props.level) {
    case UserLevel.ORGANISATION:
      return organisationRoles.value;
    case UserLevel.TENANT:
      return tenantRoles.value;
    default:
      return domainRoles.value;
  }
});

const isLoading = computed(() => {
  switch (props.level) {
    case UserLevel.ORGANISATION:
      return isOrganisationRolesLoading.value;
    case UserLevel.TENANT:
      return isTenantRolesLoading.value;
    default:
      return isDomainRolesLoading.value;
  }
});

watch(
  () => props,
  () => {
    switch (props.level) {
      case UserLevel.ORGANISATION:
        fetchOrganisationId.value = props.parentId || currentUserStore?.user?.organisationId;
        return;
      case UserLevel.TENANT:
        fetchTenantId.value = props.parentId || currentUserStore?.user?.tenantId;
        return;
      default:
        enabledDomainRoles.value = true;
        return;
    }
  },
  {
    immediate: true
  }
);
</script>

<style>
@import '@vueform/multiselect/themes/default.css';
</style>
